"use client"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Button } from "@/components/ui/button"
import { ChevronDown, ChevronUp, Menu } from "lucide-react"
import { useState } from "react"
import Link from "next/link"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Separator } from "@/components/ui/separator"
import { Badge } from "@/components/ui/badge"

type SidebarItem = {
  title: string
  href: string
  isPremium: boolean
}

type SidebarItems = {
  [category: string]: SidebarItem[]
}

interface StudyNavBarProps {
  items: SidebarItems
}

export function StudyNavBar({ items }: StudyNavBarProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [openCategories, setOpenCategories] = useState<string[]>([])

  const toggleCategory = (category: string) => {
    setOpenCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    )
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="xl:hidden">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="flex justify-between w-full py-2 px-4"
        >
          <span className="flex items-center">
            <Menu className="mr-2 h-4 w-4" />
            Study
          </span>
          {isOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ScrollArea className="h-auto max-h-[calc(100vh-10rem)]">
          <div className="p-4">
            <Accordion
              type="multiple"
              value={openCategories}
              className="w-full"
            >
              {Object.entries(items).map(([category, links]) => (
                <AccordionItem key={category} value={category}>
                  <AccordionTrigger
                    onClick={() => toggleCategory(category)}
                    className="py-2 text-base font-semibold"
                  >
                    {category}
                  </AccordionTrigger>
                  <AccordionContent>
                    <nav className="space-y-1">
                      {links.map((link) => (
                        <Link
                          key={link.href}
                          href={link.href}
                          className="flex justify-between items-center py-2 px-3 text-sm rounded-md hover:bg-accent hover:text-accent-foreground"
                          onClick={() => setIsOpen(false)}
                        >
                          <span>{link.title}</span>
                          {link.isPremium ? (
                            <Badge variant="premium">Premium</Badge>
                          ) : (
                            <Badge variant="free">Free</Badge>
                          )}
                        </Link>
                      ))}
                    </nav>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </ScrollArea>
      </CollapsibleContent>
      <Separator />
    </Collapsible>
  )
}
